/**
 * Platform HQ auth context — separate from regular HinesTime AuthContext.
 *
 * Sessions (Jun 2026): the platform JWT is delivered as an httpOnly cookie
 * (`pl_access`) so it never sits in localStorage. Only the admin profile
 * snapshot is cached locally (`pl_admin`) for an instant first render.
 * Legacy sessions with a stored `pl_token` keep working via the bearer header.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const PlatformAuthContext = createContext(null);
export const usePlatformAuth = () => useContext(PlatformAuthContext);

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

export const PlatformAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('pl_token');
    const storedAdmin = localStorage.getItem('pl_admin');
    if (storedToken || storedAdmin) {
      if (storedToken) setToken(storedToken);
      // Cookie session sentinel — platform pages gate fetches on `if (token)`.
      else if (storedAdmin) setToken('cookie-session');
      if (storedAdmin) {
        try { setAdmin(JSON.parse(storedAdmin)); } catch { /* ignore */ }
      }
      // Refresh from server — cookie sessions authenticate automatically.
      axios.get(`${API}/platform/auth/me`, {
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : undefined
      })
        .then((res) => {
          setAdmin(res.data);
          localStorage.setItem('pl_admin', JSON.stringify(res.data));
        })
        .catch((err) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            localStorage.removeItem('pl_token');
            localStorage.removeItem('pl_admin');
            setToken(null);
            setAdmin(null);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (newToken, newAdmin) => {
    // The server set the httpOnly `pl_access` cookie on sign-in — the raw JWT
    // stays out of localStorage. Kept in memory for the current SPA session.
    localStorage.removeItem('pl_token');
    localStorage.setItem('pl_admin', JSON.stringify(newAdmin));
    setToken(newToken);
    setAdmin(newAdmin);
  };

  const logout = async () => {
    try { await axios.post(`${API}/platform/auth/logout`, {}); } catch { /* best-effort */ }
    localStorage.removeItem('pl_token');
    localStorage.removeItem('pl_admin');
    setToken(null);
    setAdmin(null);
  };

  return (
    <PlatformAuthContext.Provider value={{ admin, token, loading, login, logout }}>
      {children}
    </PlatformAuthContext.Provider>
  );
};
